import React, { useEffect, useState } from 'react';
import { formatMoney, signupLabel } from './cartAnalyticsFormat';

const place = (r) => [r.city, r.country].filter(Boolean).join(', ') || '-';
const device = (r) => [r.device, r.browser].filter(Boolean).join(' / ') || '-';

function SortHeader({ field, label, sort, order, onSort, className }) {
  const active = sort === field;
  return (
    <th className={`ca-sortable ${className || ''}`} onClick={() => onSort(field)} title={`Sort by ${label}`}>
      {label}
      <span className={`ca-sort-icon${active ? ' active' : ''}`}>
        {active ? (order === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
      </span>
    </th>
  );
}

export default function CustomerTable({
  rows, summary, piiVisible, sort, order, onSort, page, onPage, loading, onSelect, search, onSearch,
}) {
  // The text is typed locally and sent to the API shortly after the user stops typing; the API searches
  // every customer in the range (not just this page). The parent clears it when filters are re-applied.
  const [text, setText] = useState(search || '');
  useEffect(() => { setText(search || ''); }, [search]);
  useEffect(() => {
    if (text.trim() === (search || '')) return undefined;
    const t = setTimeout(() => onSearch(text.trim()), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const limit = (summary && summary.limit) || 25;
  const total = (summary && summary.total_count) || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const cols = piiVisible ? 12 : 10;

  return (
    <div className="card shadow-sm mb-3 ca-customer-card">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="ca-table-section-header mb-0">
            <p className="ca-table-title">Customers and visitors</p>
            <p className="ca-table-subtitle">Individual cart activity for the selected range, sorted by {sort.replace('_', ' ')}.</p>
          </div>
          {loading && <span className="ca-loading flex-shrink-0 ml-3" style={{ fontSize: 12 }}>Loading…</span>}
        </div>
        <div className="ca-search ca-customer-search">
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder={piiVisible ? 'Search name, customer ID, email, mobile, city, country…' : 'Search name, customer ID, city, country…'}
            value={text}
            maxLength={60}
            onChange={(e) => setText(e.target.value)}
            aria-label="Search customers and visitors"
          />
          {search && (
            <button type="button" className="ca-search-clear" aria-label="Clear search" onClick={() => onSearch('')}>&times;</button>
          )}
        </div>
        {!piiVisible && (
          <div className="ca-pii-notice">
            <span>&#128274;</span>
            Email, mobile and street address are hidden for your role.
          </div>
        )}
        <div className="table-responsive">
          <table className="table table-sm ca-customers mb-0">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>Customer / visitor</th>
                <th>Sign-up</th>
                {piiVisible && <th>Email</th>}
                {piiVisible && <th>Mobile</th>}
                <th>Location</th>
                <th>Device</th>
                <SortHeader className="text-right" field="atc_count" label="Add to cart" sort={sort} order={order} onSort={onSort} />
                <th className="text-right">Carts</th>
                <SortHeader className="text-right" field="items" label="Items" sort={sort} order={order} onSort={onSort} />
                <SortHeader className="text-right" field="cart_value" label="Cart value" sort={sort} order={order} onSort={onSort} />
                <th className="text-right" style={{ whiteSpace: 'nowrap' }}>Open value</th>
                <SortHeader field="last_activity" label="Last activity" sort={sort} order={order} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={cols} className="text-center py-4 ca-empty-cell">{search ? `Nothing matches "${search}" in this range` : 'No customers or visitors found for this range'}</td></tr>
              ) : rows.map((r) => (
                <tr key={`${r.customer_key}-${r.ccy}`} className="ca-row" onClick={() => onSelect(r)}>
                  <td>
                    <div className="ca-name-primary">
                      {r.key_type === 'customer' ? (r.name || 'Customer') : r.key_type === 'visitor' ? 'Guest visitor' : 'Unknown visitor'}
                    </div>
                    <div className="ca-name-secondary">{r.customer_key}</div>
                  </td>
                  <td>
                    <div>{signupLabel(r.signup_method)}</div>
                    {r.signup_date && <div className="ca-name-secondary">{r.signup_date}</div>}
                  </td>
                  {piiVisible && <td>{r.email || '-'}</td>}
                  {piiVisible && <td>{r.mobile || '-'}</td>}
                  <td>{place(r)}</td>
                  <td>{device(r)}</td>
                  <td className="text-right">{r.atc_count}</td>
                  <td className="text-right">{r.carts}</td>
                  <td className="text-right">{r.items_qty}</td>
                  <td className="text-right">{formatMoney(r.cart_value, r.ccy)}</td>
                  <td className="text-right">{formatMoney(r.open_value, r.ccy)}</td>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.last_activity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ca-pagination">
          <span className="ca-page-info">Page {page} of {totalPages} ({total} rows)</span>
          <div className="ca-page-btns">
            <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1 || loading} onClick={() => onPage(page - 1)}>← Previous</button>
            <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages || loading} onClick={() => onPage(page + 1)}>Next →</button>
          </div>
        </div>
      </div>
    </div>
  );
}
